/**
 * Backspace out of an empty block sitting at the TOP of a table cell.
 *
 * ProseMirror's `joinBackward` walks backwards looking for something to
 * merge into. From the first child of a table cell there is nothing
 * inside the cell to merge with, and merging across the cell boundary
 * would fold one cell's content into the previous cell — so it correctly
 * refuses, and Backspace becomes a silent no-op.
 *
 * That leaves the author stuck. An empty leading paragraph in a cell is
 * not an exotic state: every real document exporter produces them
 * (Confluence in particular emits an empty `<p>` before a cell's content
 * often enough that a pasted table routinely arrives with one), and once
 * one is there the only key that removes it is FORWARD delete — which on
 * a Mac laptop is fn+Delete, so in practice the line is permanent. It is
 * also the one line an author most wants gone, because it pushes the
 * cell's real content down and misaligns the whole row.
 *
 * So: when the caret sits in an empty block that is a cell's first child
 * and the cell has other content to fall back on, Backspace drops that
 * block. The cell keeps at least one child either way, which the cell
 * content model requires, so a cell holding nothing but one empty
 * paragraph is left exactly as it was.
 */

import { TextSelection } from '@tiptap/pm/state'
import { Extension } from '@tiptap/react'

const CELL_TYPES = new Set(['tableCell', 'tableHeader'])

export const TableCellBackspace = Extension.create({
  name: 'tableCellBackspace',

  // Ahead of the default Backspace binding, which answers this position
  // by doing nothing. Everything else falls through to it untouched.
  priority: 200,

  addKeyboardShortcuts() {
    return {
      Backspace: () => {
        const { state } = this.editor
        const { selection } = state
        if (!(selection instanceof TextSelection) || !selection.empty) return false

        const $cursor = selection.$cursor
        if (!$cursor) return false

        // Only an EMPTY block, and only with the caret at its start —
        // anywhere else Backspace has real text to delete.
        const block = $cursor.parent
        if (!block.isTextblock || block.content.size > 0 || $cursor.parentOffset !== 0) {
          return false
        }

        // The block's own depth is the cursor's; its parent is the cell.
        const blockDepth = $cursor.depth
        if (blockDepth < 1) return false
        const cell = $cursor.node(blockDepth - 1)
        if (!CELL_TYPES.has(cell.type.name)) return false

        // First child only. Anywhere else, `joinBackward` already merges
        // into the block above and does the right thing.
        if ($cursor.index(blockDepth - 1) !== 0) return false

        // Never leave the cell empty: its content model requires at
        // least one block, so a lone empty paragraph has to stay.
        if (cell.childCount < 2) return false

        const from = $cursor.before(blockDepth)
        const to = $cursor.after(blockDepth)
        const tr = state.tr.delete(from, to)
        // Land the caret at the start of what is now the cell's first
        // block, which is where the author was reaching for.
        tr.setSelection(TextSelection.near(tr.doc.resolve(from), 1))
        this.editor.view.dispatch(tr.scrollIntoView())
        return true
      },
    }
  },
})
